import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { FilterSelect, HpCardHeader, SearchField, Segmented, TableFooter, Toolbar, ToolbarSpacer } from '@hp/ui';
import { useCan } from '../../api/me';
import { trpc } from '../../api/trpc';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { ErrorAlert } from '../../components/error-alert';
import { Spinner } from '../../components/ui/icons';
import { LoadMore } from '../../components/ui/load-more';
import { fmtInt } from '../../lib/format';
import {
  isLeadStatus,
  isReceivedPeriod,
  PERIOD_HOURS,
  RECEIVED_FILTERS,
  RECEIVED_PERIODS,
  type LeadAction,
  type ReceivedFilter,
  type ReceivedPeriod,
} from './labels';
import { useLeadErrorText, useNeedsRuleText } from './error-text';
import { LeadDetail } from './lead-detail';
import { invalidateLeads, useChannelOptions, useReceivedLeads, type ReceivedLead } from './queries';
import { ReceivedTable } from './received-table';

/** How long a retry / re-route result notice stays on screen. */
const NOTICE_MS = 8_000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** The period's start, fixed when the period is picked (a moving `since` would refetch on every render). */
const sinceOf = (period: ReceivedPeriod): Date | undefined =>
  // 30 days is the API's own default window; leaving it out lets the cursor keep page 1's window.
  period === '30d' ? undefined : new Date(Date.now() - PERIOD_HOURS[period] * 3_600_000);

/**
 * Tab "Received leads": the morning check. Exceptions first, a detail panel with the decision trace
 * (`?lead=<id>` deep-links it), retry for failed deliveries and re-route for leads waiting for a rule.
 */
export function ReceivedTab() {
  const { t } = useTranslation(['leads', 'common']);
  const canManage = useCan()('leads.manage');
  const describe = useLeadErrorText();
  const needsRuleText = useNeedsRuleText();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<ReceivedFilter>('exceptions');
  const [channel, setChannel] = useState('');
  const [market, setMarket] = useState<'' | 'CZ' | 'ES'>('');
  const [period, setPeriod] = useState<{ key: ReceivedPeriod; since: Date | undefined }>({
    key: '30d',
    since: undefined,
  });
  const [qInput, setQInput] = useState('');
  const q = useDebounced(qInput.trim(), 300);
  const [pending, setPending] = useState<{ lead: ReceivedLead; action: LeadAction } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const leadParam = params.get('lead');
  const selectedId = leadParam && UUID_RE.test(leadParam) ? leadParam : null;
  const select = (id: string) => {
    setNotice(null);
    setParams({ tab: 'received', lead: id });
  };
  // A result notice is read once: it goes away by itself after a few seconds.
  useEffect(() => {
    if (notice === null) return;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);
  const closeDetail = useCallback(() => setParams({ tab: 'received' }), [setParams]);

  const channels = useChannelOptions();
  const markets = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of channels.data ?? []) seen.set(c.marketCode, c.marketLabel ?? c.marketCode);
    return [...seen].flatMap(([code, label]) => (code === 'CZ' || code === 'ES' ? [{ code, label }] : []));
  }, [channels.data]);

  const list = useReceivedLeads({
    status: status === 'all' ? undefined : status,
    channel: channel || undefined,
    marketCode: market || undefined,
    since: period.since,
    q: q || undefined,
  });
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.rows) ?? [], [list.data]);
  const counts = list.data?.pages[0]?.counts;
  const total = counts ? counts[status] : 0;

  const leadName = (lead: ReceivedLead) => lead.name ?? t('received.unnamed');
  const done = (text: string) => {
    setPending(null);
    setNotice(text);
    return invalidateLeads();
  };
  const fail = () => {
    // Close the modal so the worded error above the table is not hidden behind the backdrop.
    setPending(null);
    return invalidateLeads();
  };
  const retry = useMutation(
    trpc.leads.received.retry.mutationOptions({
      onSuccess: (_res, input) =>
        done(t('received.retried', { name: pending?.lead.id === input.id ? leadName(pending.lead) : '' })),
      onError: fail,
    }),
  );
  const reroute = useMutation(
    trpc.leads.received.reroute.mutationOptions({
      onSuccess: (res, input) => {
        const name = pending?.lead.id === input.id ? leadName(pending.lead) : '';
        return done(
          res.status === 'needs_rule'
            ? t('received.reroutedStill', { name, reason: needsRuleText(res.reason) })
            : t('received.rerouted', {
                name,
                status: isLeadStatus(res.status) ? t(`general.leadStatus.${res.status}`) : res.status,
              }),
        );
      },
      onError: fail,
    }),
  );
  const running = retry.isPending ? retry.variables.id : reroute.isPending ? reroute.variables.id : null;
  const ask = (lead: ReceivedLead, action: LeadAction) => {
    setNotice(null);
    retry.reset();
    reroute.reset();
    setPending({ lead, action });
  };

  return (
    <>
      <ErrorAlert error={retry.error ?? reroute.error} describe={describe} />
      {notice && (
        <p className="bo-notice" role="status">
          {notice}
        </p>
      )}
      {leadParam !== null && selectedId === null && (
        <p className="bo-notice" role="status">
          {t('received.errors.notFound')}
        </p>
      )}
      {selectedId && (
        <LeadDetail
          key={selectedId}
          id={selectedId}
          canManage={canManage}
          busy={running === selectedId}
          onAction={ask}
          onClose={closeDetail}
        />
      )}
      <section className="hp-card">
        <HpCardHeader title={t('received.title')} subtitle={t('received.subtitle')} icon="list" />
        <Toolbar className="bo-toolbar">
          <Segmented
            ariaLabel={t('received.filterAria')}
            value={status}
            options={RECEIVED_FILTERS.map((s) => ({
              value: s,
              label: counts ? t(`received.filter.${s}`, { count: counts[s] }) : t(`received.filterBare.${s}`),
            }))}
            onChange={setStatus}
          />
          <FilterSelect
            value={channel}
            aria-label={t('received.channelAria')}
            onChange={(e) => setChannel(e.target.value)}
          >
            <option value="">{t('received.allChannels')}</option>
            {(channels.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={market}
            aria-label={t('received.marketAria')}
            onChange={(e) => {
              const v = e.target.value;
              setMarket(v === 'CZ' || v === 'ES' ? v : '');
            }}
          >
            <option value="">{t('received.allMarkets')}</option>
            {markets.map((m) => (
              <option key={m.code} value={m.code}>
                {m.label}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={period.key}
            aria-label={t('received.periodAria')}
            onChange={(e) => {
              const key = isReceivedPeriod(e.target.value) ? e.target.value : '30d';
              setPeriod({ key, since: sinceOf(key) });
            }}
          >
            {RECEIVED_PERIODS.map((p) => (
              <option key={p} value={p}>
                {t(`received.period.${p}`)}
              </option>
            ))}
          </FilterSelect>
          <ToolbarSpacer />
          <SearchField
            value={qInput}
            placeholder={canManage ? t('received.search') : t('received.searchViewer')}
            aria-label={canManage ? t('received.search') : t('received.searchViewer')}
            onChange={(e) => setQInput(e.target.value)}
          />
        </Toolbar>
        <ErrorAlert error={list.error} describe={describe} className="bo-alert--inset" />
        {list.isLoading ? (
          <p className="hp-card__body">
            <Spinner>{t('common:actions.loading')}</Spinner>
          </p>
        ) : (
          <ReceivedTable
            rows={rows}
            canManage={canManage}
            selectedId={selectedId}
            busyId={running}
            empty={status === 'exceptions' ? t('received.emptyExceptions') : t('received.empty')}
            onSelect={select}
            onAction={ask}
          />
        )}
        {list.data && (
          <TableFooter
            pagination={
              <LoadMore
                hasNextPage={list.hasNextPage}
                isFetchingNextPage={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              />
            }
          >
            {t('received.showing', { shown: fmtInt(rows.length), total: fmtInt(total) })}
          </TableFooter>
        )}
        <div className="hp-card__body">
          <p className="bo-hint">{canManage ? t('received.hint') : t('received.hintViewer')}</p>
        </div>
      </section>
      <ConfirmDialog
        open={pending !== null}
        title={pending?.action === 'reroute' ? t('received.rerouteDialog.title') : t('received.retryDialog.title')}
        confirmLabel={pending?.action === 'reroute' ? t('received.reroute') : t('received.retry')}
        busy={retry.isPending || reroute.isPending}
        onConfirm={() => {
          if (!pending) return;
          if (pending.action === 'retry') retry.mutate({ id: pending.lead.id });
          else reroute.mutate({ id: pending.lead.id });
        }}
        onCancel={() => setPending(null)}
      >
        <p>
          {pending?.action === 'reroute'
            ? t('received.rerouteDialog.body', { name: pending ? leadName(pending.lead) : '' })
            : t('received.retryDialog.body', { name: pending ? leadName(pending.lead) : '' })}
        </p>
      </ConfirmDialog>
    </>
  );
}
